/* Eliana Jewelry / Refresh 10.0.0 — cart drawer repair.
   Keeps the existing reliable Add to Cart opening behavior.
   Updates quantity, row prices, subtotal and 3+2 bar from ONE server response.
   Paste in Assets > cart-drawer.js. Do not modify Assets > cart.js. */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', () => this.close());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);

    requestAnimationFrame(() => {
      if (this.isConnected) this.classList.add('animate', 'active');
    });
    this.addEventListener('transitionend', () => {
      if (!this.classList.contains('active')) return;
      const area = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer');
      const focusTarget = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (area && focusTarget && typeof trapFocus === 'function') trapFocus(area, focusTarget);
    }, { once: true });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');
    note.setAttribute('aria-expanded', 'false');
    const body = note.nextElementSibling;
    if (body && body.id) note.setAttribute('aria-controls', body.id);
    note.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open'));
    });
    if (note.parentElement && typeof onKeyUpEscape === 'function') {
      note.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    this.productId = parsedState && parsedState.id;
    if (parsedState && this.applyCartSections(parsedState.sections)) {
      this.open();
    } else {
      // Re-request sections ONLY. Never send a second Add to Cart request.
      this.refreshContents({ open: true });
    }
  }

  applyCartSections(sections) {
    const drawerHTML = sections && sections['cart-drawer'];
    const source = this.getSectionDOM(drawerHTML, '#CartDrawer');
    const target = this.querySelector('#CartDrawer');
    if (!source || !target) return false;

    const priorScroll = this.querySelector('#CartDrawer-CartItems')?.scrollTop || 0;
    target.innerHTML = source.innerHTML;
    const updatedInner = this.querySelector('.drawer__inner');
    this.classList.toggle('is-empty', !!updatedInner?.querySelector('.drawer__inner-empty'));
    this.bindOverlay();
    const updatedItems = this.querySelector('#CartDrawer-CartItems');
    if (updatedItems) updatedItems.scrollTop = priorScroll;

    const iconHTML = sections['cart-icon-bubble'];
    const sourceIcon = this.getSectionDOM(iconHTML, '.shopify-section');
    const icon = document.getElementById('cart-icon-bubble');
    if (icon && sourceIcon) icon.innerHTML = sourceIcon.innerHTML;
    return true;
  }

  async refreshContents({ open = false } = {}) {
    try {
      const route = (window.routes && window.routes.cart_url) || '/cart';
      const separator = route.includes('?') ? '&' : '?';
      const response = await fetch(`${route}${separator}sections=cart-drawer,cart-icon-bubble`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart sections HTTP ${response.status}`);
      if (!this.isConnected) return false;
      const ok = this.applyCartSections(await response.json());
      if (open) this.open();
      return ok;
    } catch (error) {
      console.error('[Eliana] Cart refresh failed', error);
      if (open) this.open();
      return false;
    }
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) { this.activeElement = element; }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    // Avoid the default cart.js listener: it loads the main CART PAGE HTML
    // into the drawer when another integration publishes a cart update.
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'product-form' || event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    if (drawer) drawer.refreshContents();
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  // Important: this method is used by BOTH quantity edits AND trash buttons.
  // We deliberately do not call CartItems.updateQuantity: its generic renderer
  // is the source of stale subtotal/progress content in this customized drawer.
  async updateQuantity(line, quantity, name) {
    const qty = Number(quantity);
    if (!Number.isInteger(qty) || qty < 0) return;
    const drawer = this.closest('cart-drawer');
    if (!drawer || this._updating) return;
    this._updating = true;
    this.classList.add('cart__items--disabled');
    this.setAttribute('aria-busy', 'true');
    this.querySelectorAll('.quantity__button, .quantity__input, cart-remove-button button')
      .forEach((el) => { el.disabled = true; });

    try {
      const url = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: qty,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status >= 400) {
        const errorText = typeof state.description === 'string' ? state.description :
          typeof state.errors === 'string' ? state.errors : 'Could not update cart. Please try again.';
        throw new Error(errorText);
      }

      // ONE atomic section update: row quantities, prices, subtotal and progress.
      if (!drawer.applyCartSections(state.sections)) {
        if (!await drawer.refreshContents()) throw new Error('Unable to refresh cart sections');
      }

      // Restore focus if the edited row still exists.
      const newQuantityInput = document.getElementById(`Drawer-quantity-${line}`);
      if (newQuantityInput && name) newQuantityInput.focus({ preventScroll: true });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      }
    } catch (error) {
      console.error('[Eliana] Cart quantity update failed', error);
      await drawer.refreshContents();
      const errorBox = drawer.querySelector('#CartDrawer-CartErrors');
      if (errorBox) errorBox.textContent = error.message || 'Could not update cart. Please try again.';
    } finally {
      this._updating = false;
      this.classList.remove('cart__items--disabled');
      this.removeAttribute('aria-busy');
      this.querySelectorAll('.quantity__button, .quantity__input, cart-remove-button button')
        .forEach((el) => { el.disabled = false; });
    }
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);